import { useCallback, useEffect, useState } from "react";
import { fetchApi } from "@shared/infrastructure/api-client";
import { useAuth } from "@features/auth";

const DISMISSED_KEY = "yalana:push-prompt-dismissed";

async function getVapidKey(): Promise<string | null> {
  const res = await fetchApi<{ publicKey: string }>(
    "/api/notifications/vapid-public-key",
  );
  return res.publicKey || null;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const buf = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i);
  return view;
}

async function subscribePush(): Promise<void> {
  const vapidKey = await getVapidKey();
  if (!vapidKey) return;

  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;

  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    }));

  const json = sub.toJSON();
  if (!json.keys?.p256dh || !json.keys?.auth) return;

  await fetchApi("/api/notifications/push-subscriptions", {
    method: "POST",
    body: JSON.stringify({
      endpoint: sub.endpoint,
      p256dh: json.keys.p256dh,
      authKey: json.keys.auth,
    }),
  });
}

interface PushSubscriptionState {
  pushPermission: NotificationPermission;
  showPrompt: boolean;
  canEnableNotifications: boolean;
  requestPush: () => void;
  dismissPrompt: () => void;
  resetPrompt: () => void;
}

/** Manages push notification permission and subscription lifecycle. */
export function usePushSubscription(): PushSubscriptionState {
  const { user, authorizedTenants } = useAuth();

  const currentPermission = (): NotificationPermission =>
    typeof Notification !== "undefined" ? Notification.permission : "denied";

  const [permission, setPermission] = useState(currentPermission);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === "true",
  );

  const hasBrowserSupport =
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window;

  const isAuthenticated = !!user && authorizedTenants.length > 0;
  const isPermissionDefault = permission === "default";

  const showPrompt =
    isAuthenticated && hasBrowserSupport && isPermissionDefault && !dismissed;

  const canEnableNotifications =
    isAuthenticated && hasBrowserSupport && isPermissionDefault && dismissed;

  const requestPush = useCallback(() => {
    if (!hasBrowserSupport) return;
    void Notification.requestPermission().then((result) => {
      setPermission(result);
      if (result === "granted") {
        void subscribePush().catch(() => {});
      }
    });
  }, [hasBrowserSupport]);

  const dismissPrompt = useCallback(() => {
    setDismissed(true);
    localStorage.setItem(DISMISSED_KEY, "true");
  }, []);

  const resetPrompt = useCallback(() => {
    setDismissed(false);
    localStorage.removeItem(DISMISSED_KEY);
  }, []);

  // Auto-subscribe if already granted (e.g. returning user)
  useEffect(() => {
    if (!user || authorizedTenants.length === 0 || !hasBrowserSupport) return;
    if (currentPermission() === "granted") {
      void subscribePush().catch(() => {});
    }
  }, [user, authorizedTenants, hasBrowserSupport]);

  return {
    pushPermission: permission,
    showPrompt,
    canEnableNotifications,
    requestPush,
    dismissPrompt,
    resetPrompt,
  };
}
